import { useState } from 'react';

import { Col, formatNumber, PageTitle, Row } from '@dec/shared';
import { Button } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { BtnGroup } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { type ColDef, type ValueFormatterParams } from 'ag-grid-community';
import { useGetAllTaxesWitCurrentLang } from 'app/modules/taxes/hooks/useTaxes';
import * as Icon from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ActionsGrid } from '../../../components/grid';
import { type ActionColumn } from '../../../components/grid/actions-column';
import GridApplication from '../../../components/grid/gridApplication';
import { ConfirmDialog } from '../../../core/dialog';
import { notificationSuccess } from '../../../core/toast';
import { catalogs } from '../../Parameters/Catalog/Catalog/services/catalog';
import { useCatalogList } from '../../Parameters/Catalog/Catalog/services/useCatalog';
import { messages } from '../../TaxStatement/FormAdministration/translate';
import { label as labelCommon } from '../../translation';
import { FormSearchPenalties } from '../components/FormSearchPenalties';
import { useGetPenaltiesTypes, useGetValuePenaltiesTypes } from '../hooks';
import { BaseUrlPenalties, deletePenalty } from '../http';

export const ListPenalties = () => {
  const navigate = useNavigate();

  const { data: taxes, loading: l1, error: e1 } = useGetAllTaxesWitCurrentLang();
  const [{ data: penaltiesType, loading: l2, error: e2 }] = useGetPenaltiesTypes();
  const [{ data: valuesPenaltiesType, loading: l3, error: e3 }] = useGetValuePenaltiesTypes();

  const [{ data: accountTypes, loading: l4, error: e4 }] = useCatalogList(catalogs.accountType);
  const [{ data: taxPeriodicityTypes, loading: l5, error: e5 }] = useCatalogList(
    catalogs.periodicityType
  );

  const [{ data: transactionTypes, loading: l6, error: e6 }] = useCatalogList(
    catalogs.transactionType
  );

  const [toggleReload, setToggleReload] = useState(true);
  const [search, setSearch] = useState<any>({});

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();

  const actions: Array<ActionColumn> = [
    {
      icon: Icon.Trash,
      variant: 'danger',
      onClick: async (row: any) => {
        const ok = await ConfirmDialog({
          titleModal: messages(t, 'confirmTitle'),
          message: messages(t, 'confirmDeleted'),
          okButton: labelCommon('ok'),
          cancelButton: labelCommon('cancel')
        });
        if (ok) {
          await deletePenalty(row.id);
          notificationSuccess(t('common.messages.deletedSucessfully'));
          setToggleReload(!toggleReload);
        }
      }
    },
    {
      icon: Icon.Pencil,
      variant: 'primary',
      onClick: async (row: any) => {
        navigate(`/penalties/${row.id}`);
      }
    }
  ];
  const colDef: Array<ColDef> = [
    {
      headerName: t('common.label.actions'),
      cellRenderer: ActionsGrid,
      cellRendererParams: {
        actions: actions
      }
    },

    {
      headerName: t('taxpayer-payments.label.tax'),
      field: 'taxId',
      valueFormatter: (params: ValueFormatterParams) => {
        return taxes?.find((x) => x.id === params.value)?.name ?? '';
      }
    },

    {
      headerName: t('taxpayer-current-account.label.accountType'),
      field: 'accountTypeId',
      valueFormatter: (params: ValueFormatterParams) => {
        return accountTypes?.find((x) => x.id === params.value)?.name ?? '';
      }
    },
    {
      headerName: t('taxpayer-current-account.label.transactionType'),

      field: 'transactionTypeId',
      valueFormatter: (params: ValueFormatterParams) => {
        return transactionTypes?.find((x) => x.id === params.value)?.name ?? '';
      }
    },
    {
      headerName: t('taxes.label.periodicityType'),
      field: 'taxPeriodicityTypeId',
      valueFormatter: (params: ValueFormatterParams) => {
        return taxPeriodicityTypes?.find((x) => x.id === params.value)?.name ?? '';
      }
    },
    {
      //headerName: "Periodo desde",
      headerName: t('taxpayer-current-account.label.period'),
      field: 'periodFrom',
      valueFormatter: (params: ValueFormatterParams) => {
        return `${!params.data.periodTo ? `>= ` : ''}${params.data.periodFrom} ${
          params.data.periodTo ? `${t('common.label.page-to')} ` + params.data.periodTo : ''
        }`;
      }
    },
    // {
    //     headerName: "Periodo hasta",
    //     field: 'periodTo',
    // },
    {
      headerName: t('parameters-catalog.label.type'),
      field: 'penaltyType',
      valueFormatter: (params: ValueFormatterParams) => {
        return penaltiesType?.find((x) => x.id === params.value)?.name ?? '';
      }
    },
    {
      headerName: t('common.label.valueType'),
      field: 'valueType',
      valueFormatter: (params: ValueFormatterParams) => {
        return valuesPenaltiesType?.find((x) => x.id === params.value)?.name ?? '';
      }
    },
    {
      headerName: t('taxpayer-current-account.label.value'),
      field: 'value',
      valueFormatter: (params: ValueFormatterParams) => {
        return params.data.valueType === 'percentage'
          ? formatNumber(params.value * 100)
          : formatNumber(params.value);
      }
    }
  ];
  const onSubmit = (values: any) => {
    setSearch({
      taxId: values?.tax?.id,
      accountTypeId: values?.accountType?.id,
      penaltyType: values?.penaltyType?.id
    });
  };

  const newPenalty = () => {
    navigate('/penalties/create');
  };

  const isLoading = l1 || e1 || l2 || e2 || l3 || e3 || l4 || e4 || l5 || e5 || l6 || e6;

  if (isLoading) {
    return <></>;
  }

  return (
    <>
      <PageTitle title={t('penalties.title.listPenalties')} />

      <FormSearchPenalties nameForm="formSearch" onSubmit={onSubmit}>
        {(reset: any) => {
          return (
            <>
              <BtnToolbar>
                <BtnGroup>
                  <Button
                    action={ButtonAction.clean}
                    onClick={() => {
                      reset({
                        tax: null,
                        accountType: null,
                        penaltyType: null
                      });
                      setSearch({});
                    }}
                  />
                </BtnGroup>
                <BtnGroup>
                  <Button action={ButtonAction.add} onClick={newPenalty} />
                  <Button action={ButtonAction.find} variant="primary" type={'submit'} />
                </BtnGroup>
              </BtnToolbar>
            </>
          );
        }}
      </FormSearchPenalties>
      <br />
      <Row>
        <Col>
          <GridApplication
            toggleReload={toggleReload}
            columnDefs={colDef}
            url={BaseUrlPenalties}
            search={search}
          />
        </Col>
      </Row>
    </>
  );
};
